import React, { useMemo, useState } from 'react';
import { IActualDepositData } from './MatchedDepositsType';
import { StyledIncomingTableRow, StyledKabobIcon } from './StyledDeposits';
import { StyledTableRowColumn } from '../../table/StyledTable';
import { getDateString } from 'ui-toolkit/utilities/getDateString';
import { Currency } from 'ui-toolkit/components/Currency';
import { ButtonDropdown } from 'ui-toolkit/components/ButtonDropdown';
import { faEllipsisV } from '@fortawesome/pro-regular-svg-icons';
import { TDropdownElementProps } from 'ui-toolkit/components/Dropdown';
import { MatchedDepositsSidesheet } from './MatchedDepositsSidesheet';
import { UnmatchConfirmationModal } from './UnmatchConfirmationModal';

export interface IMatchedDepositRowDisplayProps {
  depositData: IActualDepositData;
  index: number;
  onUnmatch: () => void;
  hasEditPermission: boolean;
}

const matchedStatus = (depositData: IActualDepositData) => {
  switch (depositData.status) {
    case 'MISMATCHED':
      return <span className="rounded-full font-medium text-xs px-2 py-1 bg-red-100 text-red-700">MISMATCHED</span>;
    case 'MATCHED':
      if (depositData.amountCents < (depositData.actualAmountCents || 0))
        return <span className="rounded-full font-medium text-xs px-2 py-1 bg-blue-100 text-blue-700">PARTIAL</span>;
      return <span className="rounded-full font-medium text-xs px-2 py-1 bg-green-100 text-green-700">MATCHED</span>;
    default:
      return <></>;
  }
};

export const MatchedDepositRowDisplay: React.FC<IMatchedDepositRowDisplayProps> = ({
  depositData,
  index,
  onUnmatch,
  hasEditPermission,
}) => {
  const [isSidesheetOpen, setIsSidesheetOpen] = useState<boolean>(false);
  const [showUnmatchModal, setShowUnmatchModal] = useState<boolean>(false);

  const kabobItems: TDropdownElementProps[] = useMemo(
    () => [
      {
        children: 'View Details',
        onClick: () => setIsSidesheetOpen(true),
        'data-test-id': `view-details-${index}`,
      },
      {
        children: 'Unmatch',
        onClick: () => setShowUnmatchModal(true),
        'data-test-id': `unmatch-button-${index}`,
      },
    ],
    [setIsSidesheetOpen, index]
  );

  return (
    <>
      <StyledIncomingTableRow columnsLength={7}>
        <StyledTableRowColumn>{getDateString(`LLLL d, yyyy`, depositData.matchedAt)}</StyledTableRowColumn>
        <StyledTableRowColumn>{depositData.matchedBy}</StyledTableRowColumn>
        <StyledTableRowColumn>{depositData.sender}</StyledTableRowColumn>
        <StyledTableRowColumn>{depositData.type}</StyledTableRowColumn>
        <StyledTableRowColumn>
          <Currency value={depositData.amountCents} valueIsCents />
        </StyledTableRowColumn>
        <StyledTableRowColumn>{matchedStatus(depositData)}</StyledTableRowColumn>
        <div className={`justify-self-center self-center`}>
          {hasEditPermission && (
            <ButtonDropdown intent={'ghost'} isInline items={kabobItems} data-test-id={`matched-kabob`}>
              <StyledKabobIcon icon={faEllipsisV} />
            </ButtonDropdown>
          )}
        </div>
      </StyledIncomingTableRow>
      {isSidesheetOpen && (
        <MatchedDepositsSidesheet depositData={depositData} onClose={() => setIsSidesheetOpen(false)} />
      )}
      {showUnmatchModal && (
        <UnmatchConfirmationModal onUnmatch={onUnmatch} onCloseModal={() => setShowUnmatchModal(false)} />
      )}
    </>
  );
};
